import type { AvailabilityBlock } from "@/lib/availability";
import { CALENDAR_STATUS_META, type DayInfo } from "@/pages/admin/calendar/calendarUtils";
import DayAgenda from "@/pages/admin/calendar/components/DayAgenda";

interface DayViewProps {
  day: DayInfo;
  onBlock: (iso: string) => void;
  onRemoveBlock: (block: AvailabilityBlock) => void;
}

export default function DayView({ day, onBlock, onRemoveBlock }: DayViewProps) {
  const meta = CALENDAR_STATUS_META[day.status];

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border border-background-200 p-5 ${meta.cell}`}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
          {day.date.toLocaleDateString("en-GB", { weekday: "long" })}
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold">
            {day.date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </h2>
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${meta.chip}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
            {meta.label}
          </span>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-foreground-700">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-calendar-check-line text-primary-600"></i>
            {day.bookings.length} {day.bookings.length === 1 ? "booking" : "bookings"}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-lock-line text-foreground-500"></i>
            {day.blocks.length} {day.blocks.length === 1 ? "block" : "blocks"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="bg-background-50 border border-background-200 rounded-lg p-5">
            <h3 className="font-heading text-lg text-foreground-950 font-semibold">Today's schedule</h3>
            <div className="mt-4 space-y-2">
              {day.bookings.length === 0 ? (
                <p className="text-sm text-foreground-500 py-4">No bookings scheduled for this date.</p>
              ) : (
                day.bookings.map((booking) => (
                  <div key={booking.id} className="flex items-start gap-4 rounded-md bg-background-100 border border-background-200 px-4 py-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                      <i className="ri-user-3-line text-lg"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground-950">{booking.name}</p>
                      <p className="text-xs text-foreground-600 mt-0.5">{booking.subject || "Enquiry"}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-foreground-500">{booking.group_size ? `${booking.group_size} guests` : "—"}</p>
                      <p className="text-[11px] text-foreground-400 mt-0.5">{booking.status}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DayAgenda day={day} onBlock={onBlock} onRemoveBlock={onRemoveBlock} />
      </div>
    </div>
  );
}